import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useClickOutside from '@/components/Record/SubjectContent/@hooks/useClickOutside';
import useTabOutClose from '@/components/Approval/@hooks/useTabOutClose';
import useSearchRelatedApprovals from '@/hooks/approval/useSearchRelatedApprovals';
import TwoBarsIcon from '@/public/icons/approval/2bars.svg';
import TriangleDownIcon from '@/public/icons/approval/triangle-down.svg';
import { getApprovalDocUrl } from '@/utils/urls';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import ResultLayer from './ResultLayer';
import SearchBar from './SearchBar';
import SelectedList from './SelectedList';
import { SectionCaret, SectionHeader, SectionRoot } from './styles';
import type { RelatedApprovalItem, RelatedApprovalType } from './types';

const TITLE: Record<RelatedApprovalType, string> = {
  parents: '상위 품의',
  children: '하위 품의',
  related: '관련 품의',
};

const PLACEHOLDER: Record<RelatedApprovalType, string> = {
  parents: '상위 품의의 제목이나 링크를 2글자 이상 입력해 주세요.',
  children: '하위 품의의 제목이나 링크를 2글자 이상 입력해 주세요.',
  related: '관련 품의의 제목이나 링크를 2글자 이상 입력해 주세요.',
};

// 검색 호출 최소 길이 — 1글자만으로는 결과 폭이 너무 넓어 placeholder 안내(2글자 이상)와 일치시킴.
const MIN_KEYWORD_LENGTH = 2;

type RelatedApprovalSectionProps = {
  type: RelatedApprovalType;
  selectedItems: RelatedApprovalItem[];
  /** parents/children/related 어디든 이미 선택된 docId — 결과에서 제외/중복 차단 */
  globalSelectedDocIds: Set<string>;
  /** 작성/편집 중인 문서 자신의 문서번호 — 검색 결과에서 자기 자신 제외(저장 전이면 undefined) */
  currentDocumentId?: string;
  onAdd: (item: RelatedApprovalItem) => void;
  onRemove: (docId: string) => void;
};

// 캐럿 아이콘 — 상위는 triangle-down 180° 회전, 하위는 triangle-down, 관련은 2bars.
const renderCaret = (type: RelatedApprovalType) => {
  if (type === 'related') {
    return <TwoBarsIcon width={10} height={10} />;
  }
  const rotate = type === 'parents' ? 180 : 0;
  return (
    <TriangleDownIcon
      width={8}
      height={8}
      style={{ transform: `rotate(${rotate}deg)` }}
    />
  );
};

const RelatedApprovalSection = ({
  type,
  selectedItems,
  globalSelectedDocIds,
  currentDocumentId,
  onAdd,
  onRemove,
}: RelatedApprovalSectionProps) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 결과 dropdown 첫 항목으로 포커스 이동 — 검색창 ArrowDown.
  const focusFirstResult = () => {
    const first =
      containerRef.current?.querySelector<HTMLElement>('[data-result-item]');
    first?.focus();
  };

  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(containerRef, close);

  // 서버 검색 (debounce 300ms는 훅 내부) — keyword 길이 < MIN이면 호출 skip.
  const passesMinLength = keyword.trim().length >= MIN_KEYWORD_LENGTH;
  const { items, isLoading, error } = useSearchRelatedApprovals(
    passesMinLength ? keyword : '',
    { excludeDocumentIds: [currentDocumentId] },
  );

  // 이미 선택된 docId는 검색 결과에서 제외 (서버 결과 그대로 + selected만 차감).
  const visibleResults = useMemo(
    () => items.filter((it) => !globalSelectedDocIds.has(it.docId)),
    [items, globalSelectedDocIds],
  );

  const handleChange = (value: string) => {
    setKeyword(value);
    setIsOpen(value.trim().length > 0);
  };

  const handleClear = () => {
    setKeyword('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 한글 IME 조합 중 키보드 nav 무시 — 마지막 자모 중복 commit 이슈 회피(keyCode 229 는 fallback)
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      handleClear();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (visibleResults.length > 0) {
        handleSelect(visibleResults[0]);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      // 결과 dropdown이 열려 있고 항목이 있을 때 첫 항목으로 포커스 이동.
      if (isOpen && visibleResults.length > 0) {
        e.preventDefault();
        focusFirstResult();
      }
    }
  };

  // 결과 항목에서 ArrowUp/ArrowDown — 형제로 이동, 첫 항목 위로 가면 input 복귀. Esc는 닫고 input 복귀.
  const handleResultItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.nextElementSibling as HTMLElement | null;
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = e.currentTarget.previousElementSibling as HTMLElement | null;
      if (prev && prev.matches('[data-result-item]')) {
        prev.focus();
      } else {
        inputRef.current?.focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClear();
      inputRef.current?.focus();
    }
  };

  const handleSelect = (item: RelatedApprovalItem) => {
    onAdd(item);
    setKeyword('');
    setIsOpen(false);
    // 결과 클릭/Enter 시 포커스가 dropdown 항목에 남아 있어 바로 다음 검색을 못 치는 이슈 — input으로 복귀.
    inputRef.current?.focus();
  };

  const handleOpenInNewWindow = (docId: string) => {
    if (typeof window === 'undefined') return;
    // windowFeatures 를 비워 일반 새 탭으로 — feature 문자열이 있으면 팝업 간주돼 차단될 수 있음
    const win = window.open(getApprovalDocUrl(docId), '_blank');
    if (win) win.opener = null;
  };

  // 마우스 외부 클릭은 useClickOutside가 처리. Tab 키 이동은 useTabOutClose가 처리.
  const handleSectionBlur = useTabOutClose<HTMLDivElement>(isOpen, close);

  return (
    <SectionRoot ref={containerRef} onBlur={handleSectionBlur}>
      <SectionHeader>
        <SectionCaret tone={type}>{renderCaret(type)}</SectionCaret>
        <Body2 sx={{ flex: 1, color: 'var(--dk-text-strong, #4d5157)' }}>{t(TITLE[type])}</Body2>
      </SectionHeader>
      <SelectedList items={selectedItems} onRemove={onRemove} />
      <SearchBar
        ref={inputRef}
        placeholder={t(PLACEHOLDER[type])}
        value={keyword}
        onChange={handleChange}
        onClear={handleClear}
        onKeyDown={handleKeyDown}
      />
      <ResultLayer
        isOpen={isOpen}
        items={visibleResults}
        isLoading={passesMinLength && isLoading}
        hasError={!!error}
        belowMinLength={!passesMinLength}
        onSelect={handleSelect}
        onOpenInNewWindow={handleOpenInNewWindow}
        onItemKeyDown={handleResultItemKeyDown}
      />
    </SectionRoot>
  );
};

export default RelatedApprovalSection;
